import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { AppState } from "store";
import { Person } from "models/stateTypes/authState";
import { SessionHelper } from "utils/helpers";

export interface SupportTeamMember {
  name: string;
  role: string;
  imageUrl: string;
  sortOrder: number;
}

export interface SupportTeamInfo {
  name: string;
  members: SupportTeamMember[];
  skipFaqStep: boolean;
}

export interface SupportTeamContent {
  bannerTitle: string;
  bannerDescription: string;
  meetTheTeamLink: string;
  teamPageTitle: string;
  bookMeetingLink: string;
  faqTitle: string;
  faqSubtitle: string;
  sendMessageTitle: string;
  sendMessageSubtitle: string;
  callSupportTitle: string;
  callSupportSubtitle: string;
  phoneNumber: string;
  externalHeaderTitle: string;
  externalFallbackDescription: string;
}

export interface SupportTeamResponse {
  teamId: number | null;
  showPhone: boolean;
  content: SupportTeamContent | null;
  team: SupportTeamInfo | null;
  externalAgencyId: number | null;
  advisorId: number | null;
  advisorName: string | null;
  advisorImageUrl: string | null;
  advisorBookingUrl: string | null;
  agencyName: string | null;
  agencyDescription: string | null;
  agencySkipFaqStep: boolean;
}

/**
 * Hook to fetch support team data via the Azure Function.
 * Returns enriched data with Webflow CMS content (team name, members, banner).
 * Falls back gracefully when team is not in Webflow CMS.
 */
export function useSupportTeamContent(companyId: number | undefined) {
  const locale = useSelector((state: AppState) => state.common.locale.language);
  const [data, setData] = useState<SupportTeamResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(!!companyId);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!companyId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    const abortController = new AbortController();

    const fetchSupportTeam = async () => {
      setLoading(true);
      setError(null);

      try {
        const session = SessionHelper.getSessionItem<Person>("user_session");
        const accessToken = session?.authTokens?.accessToken;

        const headers: Record<string, string> = {};
        if (accessToken) {
          headers["Authorization"] = `Bearer ${accessToken}`;
          headers["X-Custom-Authorization"] = `Bearer ${accessToken}`;
        }

        const response = await fetch(`/api/support-team/${companyId}?locale=${locale}`, {
          headers,
          signal: abortController.signal,
        });

        if (!response.ok) {
          if (response.status === 404) {
            setData(null);
            return;
          }
          throw new Error(`Failed to fetch support team: ${response.statusText}`);
        }

        const json: SupportTeamResponse = await response.json();
        setData(json);
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err : new Error("Failed to fetch support team"));
        setData(null);
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchSupportTeam();

    return () => abortController.abort();
  }, [companyId, locale]);

  return { data, loading, error };
}

export type SupportTeamContentResult = ReturnType<typeof useSupportTeamContent>;
